/**
 * PATH: packages/email-hub/src/feature/pages/inbox-page/QuickTriagePalette.tsx
 * FILE_ID: EMAIL-INBOX-QUICK-TRIAGE-001
 * PURPOSE: Hotkey (t) scoped triage palette — WHO/WHAT/SIGNALS/WHERE (B-01).
 * WHY: FR-0042 master ask — triage without opening every reader; number-key
 *      accept rides existing endpoints that dual-write EmailCorrectionEvent.
 * FLOW: open on emailId → render sections from row data → 1..n accept WHERE
 *       → Esc close. Teach loop = handleConfirmSuggestion / bulkAction.
 */
import { useEffect, useMemo, useState } from 'react'
import { Check, X } from 'lucide-react'

import type { Email } from '../../hooks/useEmails.types'
import type { InboxPageController } from './useInboxPageController'

interface QuickTriagePaletteProps {
  vm: InboxPageController
}

interface WhereOption {
  key: string
  label: string
  detail: string
  run: () => Promise<void> | void
}

function sectionTitle(label: string) {
  return (
    <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </div>
  )
}

export function QuickTriagePalette({ vm }: QuickTriagePaletteProps) {
  const emailId = vm.quickTriageEmailId
  const email = useMemo(
    () =>
      emailId
        ? vm.dedupedEmails.find((row) => row.id === emailId)
          ?? vm.emails.find((row) => row.id === emailId)
          ?? null
        : null,
    [emailId, vm.dedupedEmails, vm.emails],
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const suggestion =
    (emailId ? vm.suggestionsMap?.get(emailId) : undefined)
    || email?.suggested_job
    || null

  const whereOptions: WhereOption[] = useMemo(() => {
    if (!email || !emailId) return []
    const opts: WhereOption[] = []
    if (suggestion?.job_id) {
      const jobLabel = suggestion.job_label || `Job #${suggestion.job_id}`
      opts.push({
        key: `job-${suggestion.job_id}`,
        label: jobLabel,
        detail: `AI ${suggestion.kind} · score ${suggestion.score}`,
        run: async () => {
          await vm.handleConfirmSuggestion(emailId, suggestion.job_id, jobLabel)
        },
      })
    }
    opts.push({
      key: 'complete',
      label: 'Complete (done + hide)',
      detail: 'Same as hotkey c — flips linked card DONE',
      run: async () => {
        await vm.archive(emailId, {
          folder: email.folder,
          isRead: email.is_read,
          isStarred: email.is_starred,
          complete: true,
        })
      },
    })
    opts.push({
      key: 'archive',
      label: 'Archive',
      detail: 'Same as hotkey e',
      run: async () => {
        await vm.handleArchiveRow(email)
      },
    })
    opts.push({
      key: 'junk',
      label: 'Junk',
      detail: 'Same as hotkey j',
      run: async () => {
        await vm.bulkAction([emailId], 'junk')
      },
    })
    return opts
  }, [email, emailId, suggestion, vm])

  useEffect(() => {
    if (!emailId) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        vm.setQuickTriageEmailId(null)
        return
      }
      if (busy) return
      const n = Number(e.key)
      if (!Number.isInteger(n) || n < 1 || n > whereOptions.length) return
      e.preventDefault()
      const opt = whereOptions[n - 1]
      if (!opt) return
      setBusy(true)
      setError(null)
      Promise.resolve(opt.run())
        .then(() => {
          vm.toast(`Quick Triage: ${opt.label}`, 'success')
          vm.setQuickTriageEmailId(null)
          vm.refetch()
        })
        .catch(() => {
          setError('Action failed — try again.')
          setBusy(false)
        })
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [busy, emailId, whereOptions, vm])

  if (!emailId || !email) return null

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/40 p-4 pt-[12vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Quick Triage"
      data-testid="quick-triage-palette"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) vm.setQuickTriageEmailId(null)
      }}
    >
      <div className="w-full max-w-lg rounded-xl border border-border bg-card shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground">Quick Triage · press Esc to close</div>
            <div className="truncate text-sm font-semibold text-foreground">
              {email.subject || '(no subject)'}
            </div>
          </div>
          <button
            type="button"
            className="rounded p-1 text-muted-foreground hover:bg-muted"
            aria-label="Close Quick Triage"
            onClick={() => vm.setQuickTriageEmailId(null)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-4 py-3">
          <section className="space-y-1">
            {sectionTitle('Who')}
            <p className="text-sm text-foreground">{email.from_address || 'Unknown sender'}</p>
            {email.workflow_label?.counterparty_role ? (
              <p className="text-xs text-muted-foreground">
                Role prior: {email.workflow_label.counterparty_role}
              </p>
            ) : null}
          </section>

          <section className="space-y-1">
            {sectionTitle('What')}
            <p className="text-sm text-foreground">
              {email.category
                || email.workflow_label?.category
                || 'Uncategorised'}
            </p>
            {email.body_preview ? (
              <p className="line-clamp-2 text-xs text-muted-foreground">{email.body_preview}</p>
            ) : null}
          </section>

          <section className="space-y-1">
            {sectionTitle('Signals')}
            <ul className="space-y-1 text-xs text-muted-foreground">
              {suggestion ? (
                <li>
                  Suggested job: {suggestion.job_label} ({suggestion.kind}, {suggestion.score})
                </li>
              ) : (
                <li>No AI job suggestion on this row</li>
              )}
              {email.allocation_summary?.card_id ? (
                <li>Allocated card: {email.allocation_summary.card_id}</li>
              ) : (
                <li>Not on a board yet</li>
              )}
              {typeof email.workflow_label?.confidence === 'number' ? (
                <li>
                  Classifier confidence:{' '}
                  {Math.round(email.workflow_label.confidence * 100)}%
                </li>
              ) : null}
            </ul>
          </section>

          <section className="space-y-2">
            {sectionTitle('Where (number keys)')}
            <ul className="space-y-1.5">
              {whereOptions.map((opt, idx) => (
                <li key={opt.key}>
                  <button
                    type="button"
                    disabled={busy}
                    className="flex w-full items-start gap-2 rounded-md border border-border px-2.5 py-2 text-left hover:bg-muted disabled:opacity-50"
                    onClick={() => {
                      setBusy(true)
                      setError(null)
                      Promise.resolve(opt.run())
                        .then(() => {
                          vm.toast(`Quick Triage: ${opt.label}`, 'success')
                          vm.setQuickTriageEmailId(null)
                          vm.refetch()
                        })
                        .catch(() => {
                          setError('Action failed — try again.')
                          setBusy(false)
                        })
                    }}
                  >
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded bg-muted text-[11px] font-semibold tabular-nums">
                      {idx + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-foreground">{opt.label}</span>
                      <span className="block text-xs text-muted-foreground">{opt.detail}</span>
                    </span>
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600 opacity-0 group-hover:opacity-100" />
                  </button>
                </li>
              ))}
            </ul>
          </section>

          {error ? <p className="text-sm text-rose-600">{error}</p> : null}
        </div>
      </div>
    </div>
  )
}

/** Narrow helper for tests / Storybook — build WHERE labels from an Email. */
export function buildQuickTriageWhereLabels(email: Email): string[] {
  const labels: string[] = []
  if (email.suggested_job?.job_label) labels.push(email.suggested_job.job_label)
  labels.push('Complete (done + hide)', 'Archive', 'Junk')
  return labels
}
